import { useState, useReducer, useEffect } from 'react';
import qs from 'qs';

import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import cncLogo from 'images/MyCast&CrewLogo.svg';
import { useMediaQuery } from 'react-responsive';
import Stepper, { Step } from 'components/CncUI/Stepper';

import Welcome from './steps/Welcome';
import CreatePassword from './steps/CreatePassword';
import VerifyIdentity from './steps/VerifyIdentity';
import VoiceCall from './steps/VoiceCall';
import TextMessage from './steps/TextMessage';
import AccountCreated from './steps/AccountCreated';
import AdditionalAuthentication from './steps/AdditionalAuthentication';
import OktaVerify from './OktaVerify';
import SupportFooter from 'components/SupportFooter';
import Error from './steps/Error';

import { Grid } from '@material-ui/core';
import useFeatureFlags from 'hooks/useFeatureFlags';
import usePrevious from 'hooks/usePrevious';
import Valid from 'utilities/valid';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    flexGrow: 1,
    [breakpoints.down('sm')]: {
      paddingBottom: 80,
    },
  },
  toolbar: {
    [breakpoints.up('md')]: {
      paddingLeft: 50,
      '& .CncStepper-wrapper .MuiStepper-root': {
        padding: 0,
      },
    },
  },
  header: {
    backgroundColor: palette.cncUI.default.primary,
    boxShadow: 'none',
  },
  logo: {
    flexGrow: 1,
    textAlign: 'center',
    [breakpoints.up('md')]: {
      textAlign: 'left',
    },
    '& img': {
      width: 154,
      marginTop: 2,
    },
  },
  menuButton: {
    marginLeft: -12,
    marginRight: 20,
  },
}));

const FLAG_SKIP_MFA = 'MyCnC-CreationFlow-Skip';

const AccountCreation = props => {
  const { canadaEnabled, match, location } = props;
  const logger = Logger('accountCreation');
  const { params = {} } = match;
  const { activationToken } = params;
  const { login, fromURI } = qs.parse((location.search || '?').slice(1));
  const classes = useStyles();
  const [step, setStep] = useState('Welcome');
  const prevStep = usePrevious(step);
  const reducer = (state, data) => ({ ...state, ...data });
  const [state, changeState] = useReducer(reducer, {
    email: login,
    fromURI: Valid.fromURI(fromURI),
  });
  const isMobile = useMediaQuery({
    query: '(max-width: 960px)',
  });
  const { isEnabled } = useFeatureFlags();

  useEffect(() => {
    window.scrollTo(0, 0);

    if (step === 'Error' && state.error) {
      logger.error(state.error);
    }
  }, [step]);

  useEffect(() => {
    window.onbeforeunload = function() {
      return true;
    };
  }, []);

  const activeStep =
    step === 'CreatePassword'
      ? 0
      : ['Welcome', 'Error'].includes(step)
      ? -1
      : [
          'TextMessage',
          'VoiceCall',
          'AdditionalAuthentication',
          'OktaVerify',
        ].includes(step)
      ? 2
      : step === 'AccountCreated'
      ? 3
      : 1; // update with the other pages

  const steps = {
    Welcome,
    CreatePassword,
    VerifyIdentity,
    VoiceCall,
    TextMessage,
    AccountCreated,
    AdditionalAuthentication,
    OktaVerify,
    Error,
  };

  const Component = steps[step];
  return (
    <div className={classes.root}>
      {!['Welcome'].includes(step) && (
        <AppBar position="sticky" className={classes.header}>
          <Toolbar className={classes.toolbar}>
            <Grid container spacing={0} alignItems="center">
              <Grid item md={3} xs={12}>
                <Typography
                  variant="h6"
                  color="inherit"
                  className={classes.logo}
                >
                  <img alt="logo" align="center" src={cncLogo} />
                </Typography>
              </Grid>
              <Grid item md={6} xs={12}>
                {activeStep > -1 && (
                  <Stepper green light short={isMobile} activeStep={activeStep}>
                    <Step label="Create Password" />
                    <Step label="Verify Phone" />
                    <Step label="Review Info" />
                  </Stepper>
                )}
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      )}
      <Component
        state={state}
        token={activationToken}
        changeState={changeState}
        setStep={setStep}
        step={step}
        isMobile={isMobile}
        canadaEnabled={canadaEnabled}
        skipMfa={isEnabled(FLAG_SKIP_MFA)}
      />
      <SupportFooter />
    </div>
  );
};

export default AccountCreation;
